import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCalendarData } from './CalendarDataContext';
import ReportProblem from './ReportProblem';
import { countyLabel } from '../../utils/countyDisplay';

/**
 * One county: who runs the sale, how to reach them, every sale row in full, and
 * the history of what changed.
 *
 * THE HISTORY IS IN PLAIN LANGUAGE, not as a field name and two values. The log
 * exists so a subscriber can see that a date moved or a list appeared and when,
 * which is the thing that makes a calendar worth paying for rather than a
 * snapshot. "The sale date changed from March 4 to March 18" is that. A row
 * reading "next_sale_date: 2026-03-04 to 2026-03-18" is not.
 */

const STATUS_BADGE = {
  confirmed: 'badge-success',
  expected: 'badge-info',
  cancelled: 'badge-danger',
  postponed: 'badge-warning',
  unknown: 'badge-secondary'
};

function Detail({ label, children }) {
  if (!children) return null;
  return (
    <div style={{ marginBottom: '10px' }}>
      <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{label}: </span>
      <span style={{ color: 'var(--color-text)' }}>{children}</span>
    </div>
  );
}

function CalendarCounty() {
  const { api, path } = useCalendarData();
  const { id } = useParams();
  const [county, setCounty] = useState(null);
  const [sales, setSales] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const data = await api(`/counties/${encodeURIComponent(id)}`);
        if (cancelled) return;
        setCounty(data.county);
        setSales(data.sales || []);
        setHistory(data.history || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [id, api]);

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading</p>;
  if (error) return <p style={{ color: 'var(--color-danger-text, var(--color-text))' }}>{error}</p>;
  if (!county) return null;

  return (
    <div>
      <Link to={path(`/states/${county.state_code}`)} style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
        Back to {county.state_name || county.state_code}
      </Link>

      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.35rem', margin: '12px 0 2px' }}>
        {countyLabel(county.county_name)}, {county.state_code}
      </h1>

      {county.verified_at && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginTop: 0 }}>
          Last verified {new Date(county.verified_at).toLocaleDateString()}
        </p>
      )}

      <section style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        padding: '16px',
        marginTop: '12px'
      }}>
        <Detail label="Sale run by">{county.sale_runner_label}</Detail>
        <Detail label="County seat">{county.county_seat}</Detail>
        <Detail label="Phone">
          {county.contact_phone && <a href={`tel:${county.contact_phone}`}>{county.contact_phone}</a>}
        </Detail>
        <Detail label="Email">
          {county.contact_email && <a href={`mailto:${county.contact_email}`}>{county.contact_email}</a>}
        </Detail>
        <Detail label="Website">
          {county.sale_website_url && (
            <a href={county.sale_website_url} target="_blank" rel="noopener noreferrer">
              {county.sale_website_url}
            </a>
          )}
        </Detail>
        <Detail label="Notes">{county.notes}</Detail>
      </section>

      <h2 style={{ fontSize: '1.05rem', color: 'var(--color-heading)', marginTop: '28px' }}>Sales</h2>

      {sales.length === 0 && (
        <p style={{ color: 'var(--color-text-light)' }}>No sale dates are entered for this county yet.</p>
      )}

      {sales.map(sale => (
        <section
          key={sale.id}
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            padding: '14px',
            marginBottom: '12px'
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' }}>
            <strong style={{ color: 'var(--color-heading)', flex: '1 1 auto' }}>{sale.dateLabel}</strong>
            {sale.statusLabel && (
              <span className={`badge ${STATUS_BADGE[sale.status] || 'badge-secondary'}`}>
                {sale.statusLabel}
              </span>
            )}
          </div>

          {sale.lastSaleLabel && (
            <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
              {sale.lastSaleLabel}
            </div>
          )}

          <div style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', marginTop: '4px' }}>
            {[sale.saleKindLabel, sale.platformLabel].filter(Boolean).join(', ')}
          </div>

          {/*
            Once the date has gone by, the deadline and the notes below it
            describe the sale that already happened. Shown under their own
            heading so an old registration deadline is not read as a live one.
          */}
          {(sale.registrationDeadlineLabel || sale.notes) && (
            <div style={{ marginTop: '10px' }}>
              {sale.notesLabel && (
                <div style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: 'var(--color-text-muted)',
                  marginBottom: '4px'
                }}>
                  {sale.notesLabel}
                </div>
              )}

              {sale.registrationDeadlineLabel && (
                <div style={{ color: 'var(--color-text-light)', fontSize: '0.88rem' }}>
                  Register by {sale.registrationDeadlineLabel}
                </div>
              )}

              {sale.notes && (
                <p style={{ color: 'var(--color-text)', fontSize: '0.9rem', marginTop: '6px', marginBottom: 0 }}>
                  {sale.notes}
                </p>
              )}
            </div>
          )}

          {sale.listUrl && (
            <div style={{ marginTop: '10px' }}>
              <a
                href={sale.listUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ fontSize: '0.88rem' }}
              >
                View the list
              </a>
            </div>
          )}
        </section>
      ))}

      {history.length > 0 && (
        <>
          <h2 style={{ fontSize: '1.05rem', color: 'var(--color-heading)', marginTop: '28px' }}>
            What has changed
          </h2>
          <ul style={{ paddingLeft: '18px', color: 'var(--color-text-light)' }}>
            {history.map(entry => (
              <li key={entry.id} style={{ marginBottom: '6px', fontSize: '0.9rem' }}>
                {entry.sentence}
                <span style={{ color: 'var(--color-text-muted)' }}>
                  {' '}({entry.changedOn})
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <ReportProblem page={`county:${id}`} countyId={county.id} stateCode={county.state_code} />
    </div>
  );
}

export default CalendarCounty;
